/**
 * IntegrationSetupNudge — post-login modal that surfaces missing per-rep
 * M365 / RC connections and lets the rep either connect now or dismiss
 * for the current session (returns on next login).
 *
 * Iter 237 (July 2026). Mounts once inside `MainLayout` so it triggers
 * on any authenticated page after login. Session persistence: dismissal
 * flag lives in sessionStorage keyed to the current token, and is
 * cleared in `login`/`verifyMfa` — so "next login" always re-shows.
 * On `/settings` we don't show the modal (the user can already see the
 * connect tiles inline).
 */
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { X, Mail, Phone, Sparkles, ArrowRight } from "lucide-react";

const DISMISS_KEY = "integration_nudge_dismissed_v1";

export default function IntegrationSetupNudge() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [statuses, setStatuses] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!token || !user?.id) return;
    // Don't re-nudge in the same session once the rep has dismissed.
    let dismissed = false;
    try { dismissed = sessionStorage.getItem(DISMISS_KEY) === "1"; } catch (_) {}
    if (dismissed) return;
    // Skip on the Settings page — the connect tiles are visible there.
    if (location.pathname.startsWith("/settings")) return;

    const h = { Authorization: `Bearer ${token}` };
    Promise.all([
      axios.get(`${API}/integrations/m365/oauth/status`, { headers: h })
        .then(r => r.data).catch(() => ({ configured: false, connected: false })),
      axios.get(`${API}/integrations/ringcentral/oauth/status`, { headers: h })
        .then(r => r.data).catch(() => ({ configured: false, connected: false })),
    ]).then(([m365, rc]) => {
      const m365Gap = m365?.configured && !m365?.connected;
      const rcGap   = rc?.configured   && !rc?.connected;
      if (m365Gap || rcGap) {
        setStatuses({ m365, rc, m365Gap, rcGap });
        setOpen(true);
      }
    });
    // We intentionally re-run on route change so the modal appears if
    // the rep lands on a non-settings page after login (deep links, etc.).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, user?.id, location.pathname]);

  const dismiss = () => {
    try { sessionStorage.setItem(DISMISS_KEY, "1"); } catch (_) {}
    setOpen(false);
  };

  const goConnect = (which) => {
    dismiss();
    navigate(`/settings?section=integrations${which ? `&focus=${which}` : ""}`);
  };

  if (!open || !statuses) return null;
  const { m365Gap, rcGap } = statuses;

  return (
    <div
      data-testid="integration-nudge-modal"
      style={{
        position: "fixed", inset: 0, zIndex: 220,
        background: "rgba(11,17,32,0.55)",
        backdropFilter: "blur(4px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 24,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) dismiss(); }}
    >
      <div
        style={{
          width: 560, maxWidth: "100%",
          background: "#fff",
          borderRadius: 16, overflow: "hidden",
          boxShadow: "0 24px 60px -16px rgba(0,0,0,0.35)",
        }}
      >
        <header
          style={{
            padding: "16px 22px",
            borderBottom: "0.5px solid #eef2f7",
            display: "flex", alignItems: "center", justifyContent: "space-between",
            background: "linear-gradient(135deg, #EEF4FF 0%, #FFFFFF 60%)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Sparkles style={{ width: 16, height: 16, color: "#2563eb" }} />
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#0F172A" }}>
              Finish setting up your workspace
            </h3>
          </div>
          <button
            onClick={dismiss}
            data-testid="integration-nudge-close"
            aria-label="Close"
            style={{ background: "transparent", border: 0, cursor: "pointer", color: "#64748b", padding: 4 }}
          >
            <X style={{ width: 16, height: 16 }} />
          </button>
        </header>

        <div style={{ padding: "18px 22px" }}>
          <div style={{ fontSize: 12, color: "#334155", lineHeight: 1.6, marginBottom: 14 }}>
            You&apos;re missing {(m365Gap && rcGap) ? "two connections" : "a connection"} that the CRM
            uses to send calendar invites and generate meeting links. Without them,
            attendees may not receive Outlook invites, and &quot;Generate RC link&quot; will fail
            silently when you schedule meetings.
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {m365Gap && (
              <ConnectRow
                testid="integration-nudge-m365"
                icon={<Mail style={{ width: 16, height: 16, color: "#2563eb" }} />}
                title="Microsoft 365 (Outlook)"
                desc="Sync emails to/from your leads and send real Outlook calendar invites when you book meetings."
                onConnect={() => goConnect("m365")}
                accent="#2563eb"
                bg="#EFF6FF"
                border="#BFDBFE"
              />
            )}
            {rcGap && (
              <ConnectRow
                testid="integration-nudge-rc"
                icon={<Phone style={{ width: 16, height: 16, color: "#FF6A00" }} />}
                title="RingCentral"
                desc="Make and receive calls from your own line, auto-log call activity, and generate real RC meeting links."
                onConnect={() => goConnect("rc")}
                accent="#FF6A00"
                bg="#FFF7ED"
                border="#FED7AA"
              />
            )}
          </div>
        </div>

        <footer
          style={{
            padding: "12px 22px",
            borderTop: "0.5px solid #eef2f7",
            display: "flex", alignItems: "center", justifyContent: "space-between",
            gap: 10, background: "#F8FAFC",
          }}
        >
          <div style={{ fontSize: 11, color: "#64748b" }}>
            We&apos;ll remind you again on your next sign-in.
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={dismiss}
              data-testid="integration-nudge-skip"
              style={{
                background: "transparent", color: "#475569",
                border: "0.5px solid #cbd5e1", borderRadius: 8,
                padding: "7px 14px", fontSize: 12, fontWeight: 600,
                cursor: "pointer", fontFamily: "inherit",
              }}
            >
              Skip for now
            </button>
            <button
              onClick={() => goConnect(null)}
              data-testid="integration-nudge-connect"
              style={{
                background: "#2563eb", color: "#fff",
                border: 0, borderRadius: 8,
                padding: "7px 14px", fontSize: 12, fontWeight: 700,
                cursor: "pointer", fontFamily: "inherit",
                display: "inline-flex", alignItems: "center", gap: 6,
              }}
            >
              Open Settings
              <ArrowRight style={{ width: 12, height: 12 }} />
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

function ConnectRow({ icon, title, desc, onConnect, accent, bg, border, testid }) {
  return (
    <div
      data-testid={testid}
      style={{
        display: "flex", alignItems: "center", gap: 12,
        padding: "12px 14px",
        background: bg, border: `0.5px solid ${border}`, borderRadius: 10,
      }}
    >
      <div style={{
        width: 34, height: 34, borderRadius: 8, background: "#fff",
        display: "flex", alignItems: "center", justifyContent: "center",
        border: `0.5px solid ${border}`,
      }}>{icon}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "#0F172A" }}>{title}</div>
        <div style={{ fontSize: 11, color: "#475569", marginTop: 2, lineHeight: 1.5 }}>{desc}</div>
      </div>
      <button
        onClick={onConnect}
        data-testid={`${testid}-connect-btn`}
        style={{
          background: accent, color: "#fff",
          border: 0, borderRadius: 6,
          padding: "6px 12px", fontSize: 11, fontWeight: 700,
          cursor: "pointer", fontFamily: "inherit",
          display: "inline-flex", alignItems: "center", gap: 5,
          whiteSpace: "nowrap",
        }}
      >
        Connect
        <ArrowRight style={{ width: 11, height: 11 }} />
      </button>
    </div>
  );
}
